import { Skeleton } from 'antd';
import { useLazyGetAreasQuery, useLazyGetStreetsQuery } from 'app/services/addresses';
import { AreaOption, StreetOption } from 'app/services/addresses/types';
import { selectors } from 'app/services/me';
import { useGetCountriesQuery } from 'app/services/sites';
import { useLazyGetUnitRepresentativesQuery, useLazyGetUnitsQuery } from 'app/services/units';
import { Unit } from 'app/services/units/types';
import { QuickSearch } from 'app/types/search';
import { DataForm, FieldProps, FieldType } from 'components';
import AddressAttribution from 'components/DataForm/components/AddressAttribution';
import { UnitType } from 'constants/unit';
import _ from 'lodash';

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

type SiteFormProps = {
  handleSubmit: (value: any) => void;
  meta: any;
  initialValues: any;
  redirectTo?: string;
  formId: string;
}

type SelectedArea = { name?: string, filter?: string };

const SiteForm = ({ initialValues, redirectTo, handleSubmit, meta, formId }: SiteFormProps) => {
  const [ countryId, setCountryId ] = useState<number | undefined>(initialValues?.CountryId);
  const [ selectedArea, setSelectedArea ] = useState<SelectedArea>({ name: initialValues?.AreaName });
  const [ searchStreets ] = useLazyGetStreetsQuery();
  const [ searchAreas ] = useLazyGetAreasQuery();
  const [ addresses, setAddresses ] = useState<StreetOption[]>([]);
  const [ areas, setAreas ] = useState<AreaOption[]>([]);

  const [ searchUnits ] = useLazyGetUnitsQuery();
  const [ units, setUnits ] = useState<QuickSearch[]>([]);
  const userUnit = useSelector(selectors.getUnit);

  const [ getUnitRepresentatives ] = useLazyGetUnitRepresentativesQuery();
  const { data: countries } = useGetCountriesQuery();

  useEffect(() => {
    loadInitialValues();
  }, []);

  const loadInitialValues = async () => {
    if (userUnit?.Type === UnitType.CLIENT) return;

    const initialUnits = await searchUnits({ type: UnitType.CLIENT }).unwrap();
    if (!initialUnits) return;

    handleSetUnits(initialUnits);
  };

  const handleSetUnits = (data: Unit[]) => {
    const transFormedUnits = _.map(data, (value: Unit) => (
      {
        label: `${value.Name} (${value.RegistryCode})`,
        value: `${value.Name} (${value.RegistryCode})`,
        UnitId: value.Id
      }
    ));
    setUnits(transFormedUnits);
  };

  const handleSelect = async (value: any) => {
    const representative = await getUnitRepresentatives(value.UnitId).unwrap();

    return {
      MainDemolisherRepresentative: representative?.FirstLastName,
      MainDemolisherEmail: representative?.Email,
      MainDemolisherPhone: representative?.PhoneNumber,
      UnitId: value.UnitId
    };
  };

  const handleCountryChange = (id: number) => {
    setCountryId(id);
    setSelectedArea({});
    setAreas([]);
    setAddresses([]);
  };

  // A picked street carries its municipality.
  const handleStreetSelect = (option?: StreetOption) => {
    if (option?.area) setSelectedArea({ name: option.area });

    return { AreaName: option?.area };
  };

  if (!countries) return <Skeleton active />;

  const selectedCountryId = countryId ?? countries[0]?.Id;

  const fields: FieldProps[] = [
    {
      name: 'CountryId',
      required: true,
      type: FieldType.SELECT,
      selectProps: { options: countries, labelKey: 'LocalName', valueKey: 'Id' },
      // A new site starts in the first country, Finland.
      initialValue: initialValues?.CountryId ? undefined : countries[0]?.Id,
      onChange: handleCountryChange,
      // Suggestions belong to a country: changing it clears the picked area and street.
      setFields: { keys: [ 'AreaName', 'Address' ], onSelect: () => ({}) }
    },
    {
      name: 'AreaName',
      required: true,
      type: FieldType.QUICK_SEARCH,
      searchProps: {
        searchCallback: (search: string) => searchAreas({ search, countryId: selectedCountryId }),
        setDataCallback: setAreas,
        options: areas,
        minLength: 3
      },
      onChange: (name: string, option?: AreaOption) => setSelectedArea({ name, filter: option?.filter }),
    },
    {
      name: 'Address',
      required: true,
      type: FieldType.QUICK_SEARCH,
      searchProps: {
        searchCallback: (search: string) => searchStreets({ search, countryId: selectedCountryId, area: selectedArea.name, areaFilter: selectedArea.filter }),
        setDataCallback: setAddresses,
        options: addresses,
        minLength: 3
      },
      setFields: { keys: [ 'AreaName' ], onSelect: handleStreetSelect, skipUndefined: true },
      extra: <AddressAttribution />
    },
    { name: 'Building', required: true, noSpaces: true },
    { name: 'MainContractorName' },
    { name: 'MainContractorRepresentative' },
    { name: 'MainContractorEmail' },
    { name: 'MainContractorPhone' },
    {
      name: 'AdditionalRepresentatives', dynamicFields: [
        { name: 'Name', required: true },
        { name: 'Email', required: true },
      ], type: FieldType.DYNAMIC
    },
    {
      name: 'MainDemolisherName',
      type: FieldType.SELECT_SEARCH,
      searchProps: {
        searchCallback: (value: string) => searchUnits({ type: UnitType.CLIENT, name: value }),
        setDataCallback: (data: Unit[]) => handleSetUnits(data),
        options: units
      },
      setFields: {
        keys: [
          'MainDemolisherRepresentative',
          'MainDemolisherEmail',
          'MainDemolisherPhone',
          'UnitId'
        ],
        onSelect: (value: any) => handleSelect(value)
      },
      disabled: userUnit?.Type === UnitType.CLIENT
    },
    { name: 'MainDemolisherRepresentative' },
    { name: 'MainDemolisherEmail' },
    { name: 'MainDemolisherPhone' },
    { name: 'UnitId', type: FieldType.HIDDEN }
  ];

  return <DataForm
    id={formId}
    entity="site"
    onSubmit={handleSubmit}
    fields={fields}
    redirectTo={redirectTo}
    meta={meta}
    initialValues={initialValues}
  />;
};

export default SiteForm;
